import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Building2, CircleAlert, LockKeyhole, RefreshCw } from "lucide-react";
import { useState, type FormEvent, type ReactNode } from "react";
import { getCurrentUser, login } from "../lib/auth";
import { usePwa } from "./PwaProvider";

const demoProfiles = [
  ["director", "Директор", "director@apartos.local", "Director123!"],
  ["manager", "Менеджер", "manager.demo@apartos.local", "Manager123!"],
  ["administrator", "Главный администратор", "admin@apartos.local", "Admin123!"],
  ["concierge", "Консьерж", "concierge.demo@apartos.local", "Concierge123!"],
  ["accountant", "Бухгалтер", "accountant.demo@apartos.local", "Accountant123!"],
  ["housekeeper", "Горничная", "oksana.demo@apartos.local", "Cleaner123!"],
  ["room_service", "Рум-сервис", "service.demo@apartos.local", "Service123!"],
] as const;

export function AuthGate({ children }: { children: ReactNode }) {
  const client = useQueryClient();
  const { checkingConnection, retryConnection } = usePwa();
  const { data, isPending, isError } = useQuery({
    queryKey: ["auth", "me"],
    queryFn: getCurrentUser,
    retry: false,
    networkMode: "always",
    staleTime: 60_000,
  });
  const [email, setEmail] = useState(import.meta.env.DEV ? "director@apartos.local" : "");
  const [password, setPassword] = useState(import.meta.env.DEV ? "Director123!" : "");
  const mutation = useMutation({
    mutationFn: () => login(email, password),
    onSuccess: (user) => client.setQueryData(["auth", "me"], user),
  });

  if (isPending) return <div className="auth-loading"><span /></div>;
  if (isError) return <div className="auth-screen"><section className="auth-card">
    <div className="auth-brand"><div><Building2 size={21} /></div><strong>Qadim <span>Yard</span></strong></div>
    <div className="auth-icon tone-orange"><CircleAlert size={22} /></div>
    <h1>Не удалось связаться с сервером</h1>
    <p>CRM продолжит проверять соединение автоматически. Можно повторить запрос сейчас.</p>
    <button className="secondary-button" type="button" disabled={checkingConnection} onClick={async () => {
      await retryConnection();
      void client.refetchQueries({ queryKey: ["auth", "me"] });
    }}><RefreshCw size={15} className={checkingConnection ? "is-spinning" : ""} />{checkingConnection ? "Проверяем…" : "Повторить"}</button>
  </section></div>;
  if (data) return children;

  const submit = (event: FormEvent) => {
    event.preventDefault();
    mutation.mutate();
  };

  return <div className="auth-screen">
    <section className="auth-card">
      <div className="auth-brand"><div><Building2 size={21} /></div><strong>Qadim <span>Yard</span></strong></div>
      <div className="auth-icon"><LockKeyhole size={22} /></div>
      <h1>Вход в систему</h1><p>Управление объектом размещения</p>
      <form onSubmit={submit}>
        {import.meta.env.DEV && <label className="field auth-demo-profile"><span>Проверить интерфейс роли</span><select defaultValue="director" onChange={(event) => {
          const profile = demoProfiles.find(([key]) => key === event.target.value);
          if (profile) { setEmail(profile[2]); setPassword(profile[3]); }
        }}>{demoProfiles.map(([key, label]) => <option value={key} key={key}>{label}</option>)}</select><small>Локальные тестовые аккаунты. В production этот список скрыт.</small></label>}
        <label className="field"><span>Email</span><input name="email" autoComplete="username" spellCheck={false} type="email" required value={email} onChange={(event) => setEmail(event.target.value)} /></label>
        <label className="field"><span>Пароль</span><input name="password" autoComplete="current-password" type="password" required minLength={8} value={password} onChange={(event) => setPassword(event.target.value)} /></label>
        {mutation.error && <div className="form-error" role="alert"><CircleAlert size={15} />{mutation.error.message}</div>}
        <button className="primary-button" disabled={mutation.isPending}>{mutation.isPending ? "Входим…" : "Войти"}</button>
      </form>
    </section>
  </div>;
}
